import { useCallback, useEffect, useRef } from "react";
import styles from "./media-viewer-dialog.module.css";
import { MdArrowBackIosNew, MdArrowForwardIos } from "react-icons/md";
import { useDialogRouting } from "@/hooks/use-dialog-routing";

export type ViewerItem = {
  url: string;
  type: "image" | "video";
  thumbnailUrl: string | null;
};

type Props = {
  items: ViewerItem[];
  activeIndex: number;
  setActiveIndex: (i: number) => void;
  onClose: () => void;
};

export default function MediaViewerDialog({
  items,
  activeIndex,
  setActiveIndex,
  onClose,
}: Props) {
  const safeIndex = Math.min(activeIndex, items.length - 1);
  const current = items[safeIndex];

  useDialogRouting("MediaViewerDialog", true, onClose);

  // 2. Keep ONLY the Arrow Key logic in a clean, dedicated useEffect.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") {
        setActiveIndex((safeIndex + 1) % items.length);
      }
      if (e.key === "ArrowLeft") {
        setActiveIndex((safeIndex - 1 + items.length) % items.length);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [safeIndex, items.length, setActiveIndex]);

  if (!current) return null;

  return (
    <div className={styles.wrapper} onClick={onClose}>
      <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
        <button
          className={styles.close}
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
        >
          ✕
        </button>
        <button
          className={styles.prev}
          onClick={(e) => {
            e.stopPropagation();
            setActiveIndex((safeIndex - 1 + items.length) % items.length);
          }}
        >
          <MdArrowBackIosNew />
        </button>

        <button
          className={styles.next}
          onClick={(e) => {
            e.stopPropagation();
            setActiveIndex((safeIndex + 1) % items.length);
          }}
        >
          <MdArrowForwardIos />
        </button>
        {/* PREVIEW */}
        <div className={styles.preview}>
          {current.type === "image" ? (
            <img src={current.url} alt="preview" />
          ) : (
            <video
              src={current.url}
              controls
              autoPlay
              className={styles.viewerVideo}
            />
          )}
        </div>

        {/* THUMB STRIP */}
        <div className={styles.thumbs}>
          {items.map((item, i) => (
            <div
              key={i}
              className={`${styles.thumb} ${
                i === safeIndex ? styles.active : ""
              }`}
              onClick={() => setActiveIndex(i)}
            >
              <img src={item.thumbnailUrl || item.url} alt="" />

              {/* play icon for videos */}
              {item.type === "video" && (
                <div className={styles.thumbPlay}>▶</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
